import Icon from 'components/atoms/Icon';
import {
  InterviewStageFragment,
  ProjectAction,
  ProjectJobRoleFragment,
  ShortlistStageBaseFragment,
} from 'generated/graphql';
import { TProjectPolicyActions } from 'hooks/useProjectPolicyActions/useProjectPolicyActions.helpers';
import { useQueryParams } from 'hooks/useQueryParams';
import { StageType } from 'views/Project/components/Stages/Stage';
import { Button, DropdownMenu, Tooltip } from '@spotted-zebra-uk/ui-components';

const MAX_INTERVIEW_STAGES = 10;
// Admin is deliberately looser than the company app: a role can hold several
// assessment stages with no per-company entitlement, capped like interviews.
const MAX_ASSESSMENT_STAGES = 10;
// The AI interview stage is a kind of assessment stage, but a role runs at
// most one of them.
const MAX_AI_INTERVIEW_STAGES = 1;
const MAX_SHORTLIST_STAGES = 1;

type InsertStageButtonProps = {
  projectJobRoles: ProjectJobRoleFragment[];
  assessmentKindStageCount: number;
  aiInterviewKindStageCount: number;
  projectPolicyActions: TProjectPolicyActions;
  handleEditStage: (type: StageType) => void;
  handleInsertStage: () => void;
  handleInsertAiInterviewStage: () => void;
  disabled: boolean;
  interviewStages: InterviewStageFragment[];
  shortlistStages: ShortlistStageBaseFragment[];
  isShortlistingEnabled: boolean;
};
const InsertStageButton = ({
  projectJobRoles,
  projectPolicyActions,
  handleEditStage,
  handleInsertStage,
  handleInsertAiInterviewStage,
  disabled,
  assessmentKindStageCount,
  aiInterviewKindStageCount,
  interviewStages,
  shortlistStages,
  isShortlistingEnabled,
}: InsertStageButtonProps) => {
  const [params] = useQueryParams(['devmode']);
  const isDevMode = params.devmode === 'true';

  const options = [
    {
      label: 'Interview stage',
      icon: <Icon icon="groups" />,
      disabled:
        !projectPolicyActions[ProjectAction.CreateInterviewStage] ||
        interviewStages.length >= MAX_INTERVIEW_STAGES,
      onClick: handleInsertStage,
    },
    {
      label: 'Assessment stage',
      icon: <Icon icon="assessments" />,
      disabled:
        assessmentKindStageCount >= MAX_ASSESSMENT_STAGES ||
        !projectPolicyActions[ProjectAction.CreateAssessmentStage],
      onClick: () => handleEditStage('assessment'),
    },
    {
      label: 'AI Interview stage',
      icon: <Icon icon="ai_interview" />,
      disabled:
        aiInterviewKindStageCount >= MAX_AI_INTERVIEW_STAGES ||
        !projectPolicyActions[ProjectAction.CreateAssessmentStage],
      onClick: handleInsertAiInterviewStage,
    },
  ];

  if (isDevMode && isShortlistingEnabled) {
    options.push({
      label: 'Shortlist stage',
      icon: <Icon icon="add_black" />,
      disabled:
        !projectPolicyActions[ProjectAction.CreateShortlistStage] ||
        shortlistStages.length >= MAX_SHORTLIST_STAGES,
      onClick: () => handleEditStage('shortlist'),
    });
  }

  return !projectJobRoles.length ? (
    <Tooltip
      content="Add a Role Skills Profile to insert a stage"
      id="stage-header__insert-button-tooltip"
    >
      <div id="stage-header__insert-button-tooltip-trigger">
        <Button
          size="medium"
          disabled
          data-testid="stage-header__insert-button"
        >
          Insert stage
        </Button>
      </div>
    </Tooltip>
  ) : (
    <DropdownMenu
      renderButton={(_, __, handleTriggerDropdown) => (
        <Button
          size="medium"
          onClick={handleTriggerDropdown}
          disabled={disabled}
          data-testid="stage-header__insert-button"
        >
          Insert stage
        </Button>
      )}
      options={options}
    />
  );
};

export default InsertStageButton;
